import { Button, Form, IFormRender, IFormValidation, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { ClassName } from "types/ClassName";
import BitwiseGroupFormField from 'components/foundation/form-fields/BitwiseGroupFormField/BitwiseGroupFormField';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { useToastDispatch } from 'contexts/ToastContext';
import { usePositionUpdate } from 'hooks';
import { useMemo, useRef } from 'react';
import { IPositionBitwiseKey } from 'types';
import IPosition from 'types/Position';
import { t } from 'utils/localize';
import { defaultBitwisePayloads } from '../util/constants';

export interface IPositionDetailsBitwiseFormProps {
  position: IPosition;
  title: string;
  currentValue: number;
  subtitle: string;
  disabledMask?: number;
  dataKey: IPositionBitwiseKey;
  onSubmit: (value: number) => void;
  validations?: Array<IFormValidation<number>>;
  className?: ClassName;
}

interface IFormData {
  bitwise: number;
}

export default function PositionDetailsBitwiseForm({
  position,
  onSubmit,
  title,
  subtitle,
  validations,
  currentValue,
  disabledMask,
  dataKey,
  className,
}: IPositionDetailsBitwiseFormProps) {
  const initialFormData = useRef({ bitwise: currentValue });
  const [updateKey] = usePositionUpdate();
  const toastDispatch = useToastDispatch();

  const payloads = useMemo(() => {
    if (disabledMask) {
      return defaultBitwisePayloads.map((payload) => ({
        ...payload,
        // tslint:disable-next-line: no-bitwise
        enabled: (disabledMask & payload.value) === 0 || (dataKey === 'timeEvents' && payload.name === 'Self'),
      }));
    } else {
      return defaultBitwisePayloads;
    }
  }, [dataKey, disabledMask]);

  function renderFormFields(form: IFormRender<IFormData>) {
    return (
      <>
        <BitwiseGroupFormField name="bitwise" form={form} payloads={payloads} validations={validations} />

        <Button type="primary" onClick={form.handleSubmit}>
          {t('Update')}
        </Button>
      </>
    );
  }

  async function onFormSubmit(value: IFormData) {
    if (value.bitwise !== currentValue) {
      try {
        await updateKey(position.id, dataKey, value.bitwise);
        if (dataKey === 'manageTimeEntries') {
          const currentTimeEvents = position.timeEvents ?? 0;
          const newTimeEvents = currentTimeEvents | value.bitwise;
          if (newTimeEvents !== currentTimeEvents) {
            await updateKey(position.id, 'timeEvents', newTimeEvents);
          }
        } else if (dataKey === 'manageSchedule') {
          const currentViewSchedule = position.viewSchedule ?? 0;
          const newViewSchedule = currentViewSchedule | value.bitwise;
          if (newViewSchedule !== currentViewSchedule) {
            await updateKey(position.id, 'viewSchedule', newViewSchedule);
          }
        } else if (dataKey === 'manageMediaCollection') {
          const currentViewPhotos = position.viewMediaCollection ?? 0;
          const newViewPhotos = currentViewPhotos | value.bitwise;
          if (newViewPhotos !== currentViewPhotos) {
            await updateKey(position.id, 'viewMediaCollection', newViewPhotos);
          }
        }
      } catch (e) {
        toastDispatch({
          type: 'OPEN',
          payload: {
            theme: Theme.DANGER,
            label: t('There was a problem updating your position. Please try again later.'),
          },
        });
      }
    }

    onSubmit(value.bitwise);
  }

  const classes = classNames('position-details-bitwise-form', className);

  return (
    <>
      <HeaderText title={title} className={classes}>
        {subtitle}
        <Form className="mt-2" render={renderFormFields} onSubmit={onFormSubmit} data={initialFormData.current} />
      </HeaderText>
    </>
  );
}
